import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  Pagination,
  Select,
  Table,
  Typography,
  Space,
  message,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { format } from "date-fns";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import UpdatedByCell from "../../components/UpdatedByCell";
import TrainingTypeTag from "../../components/TrainingTypeTag";
import StatusTag from "../../components/StatusTag";
import { trainingVerificationService } from "../../services/training-verification.service";
import {
  TRAINING_TYPE_OPTIONS,
  TABLE_CONFIG,
} from "../../utils/trainingCatalog.constants";
import { fetchProvinces } from "../../services/province.service";

export default function TrainingVerificationIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [form] = Form.useForm();
  const [keyword, setKeyword] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const debouncedKeyword = useDebounce(keyword, 500);

  const [params, setParams] = useState({
    limit: 10,
    page: 1,
    filter: [],
  });

  const trainingCategory = Form.useWatch("category", form);
  const province = Form.useWatch("province", form);

  const { data: provinces = [] } = useQuery({
    queryKey: ["provinces"],
    queryFn: fetchProvinces,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const provinceOptions = useMemo(
    () =>
      provinces.map((prov) => ({
        label: prov.nama,
        value: prov.nama,
      })),
    [provinces],
  );

  useEffect(() => setTitle("Verifikasi Pelatihan"), [setTitle]);

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      query: debouncedKeyword || undefined,
      category: trainingCategory || undefined,
      province: province || undefined,
    }));
  }, [debouncedKeyword, trainingCategory, province]);

  const { data, isLoading, isRefetching, error } = useQuery({
    queryKey: ["trainingVerification", params],
    queryFn: () => trainingVerificationService.fetchAll(params),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (page, limit) =>
    setParams((prev) => ({ ...prev, page, limit }));

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await trainingVerificationService.exportExcel({
        query: params.query,
        category: params.category,
        province: params.province,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || "peserta-pelatihan.xlsx";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("File berhasil diunduh");
    } catch {
      message.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = useMemo(() => {
    const currentPage = data?.pagination?.page || params.page || 1;

    return [
      {
        title: "No",
        align: "center",
        width: 60,
        fixed: "left",
        render: (_, __, i) => (currentPage - 1) * params.limit + i + 1,
      },
      {
        title: "Nama Peserta",
        dataIndex: "name",
        width: 300,
        fixed: "left",
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: "Nama Lembaga",
        dataIndex: "institution",
        width: 220,
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <Typography.Text style={{ marginBottom: 0, fontWeight: 600 }}>
              {record.institution || "-"}
            </Typography.Text>
            {record.province && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: "13px", fontStyle: "italic" }}
              >
                {record.province}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: "Judul & Jadwal Pelatihan",
        dataIndex: "title",
        width: 250,
        // render: (val) => val ?? "-",
        render: (_, record) => (
          <>
            <Space direction="vertical" size={2}>
              <Typography.Text style={{ marginBottom: 0, fontWeight: 600 }}>
                {record.title ?? "-"}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: "13px", fontStyle: "italic" }}
              >
                {record.trainingStartDate} s/d {record.trainingEndDate}
              </Typography.Text>
            </Space>
          </>
        ),
      },
      {
        title: "Negara Tujuan",
        dataIndex: "requestedCountry",
        width: 150,
      },
      {
        title: "Jenis Pelatihan",
        dataIndex: "category",
        width: 140,
        render: (type) => <TrainingTypeTag type={type} />,
      },
      {
        title: "Sektor Pekerjaan",
        dataIndex: "sectoral",
        width: 220,
        ellipsis: true,
      },
      // {
      //   title: "Sumber Biaya",
      //   dataIndex: "costSources",
      //   width: 220,
      //   render: (val) => (val != null ? COST_SOURCE_LABELS[val] : "-"),
      // },
      {
        title: "Tanggal Pengajuan",
        dataIndex: "submissionDate",
        width: 150,
        render: (val) => (
          <Typography.Text style={{ fontSize: "13px", fontStyle: "italic" }}>
            {val ? format(new Date(val), "dd/MM/yyyy") : "-"}
          </Typography.Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 140,
        render: (s) => <StatusTag status={s} />,
      },
      {
        title: "Update By",
        width: 180,
        render: (val) => (val?.updatedBy ? <UpdatedByCell value={val} /> : "-"),
      },
      {
        title: "Aksi",
        align: "right",
        fixed: "right",
        width: 80,
        render: (r) => (
          <Button
            icon={<Icon icon="solar:eye-bold-duotone" width={18} height={18} />}
            onClick={() => navigate(`/training-verification/${r.uuid}`)}
          />
        ),
      },
    ];
  }, [data?.pagination?.page, params.page, params.limit, navigate]);

  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      <Form form={form}>
        <Flex
          justify="space-between"
          align="center"
          gap={12}
          wrap="wrap"
          style={{ paddingInline: 24, marginBottom: 24 }}
        >
          <Input
            style={{ width: breakpoint.md ? 300 : "100%", minWidth: 200 }}
            prefix={<Icon icon="hugeicons:search-01" width={16} height={16} />}
            placeholder="Cari nama pelatihan..."
            allowClear
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Flex gap={12} wrap="wrap">
            <Form.Item noStyle name="category">
              <Select
                allowClear
                placeholder="Jenis Pelatihan"
                style={{ width: breakpoint.md ? 160 : 140 }}
                options={TRAINING_TYPE_OPTIONS}
              />
            </Form.Item>
            <Form.Item noStyle name="province">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Provinsi"
                style={{ width: breakpoint.md ? 230 : 200 }}
                options={provinceOptions}
              />
            </Form.Item>
            <Button
              type="primary"
              icon={
                <Icon icon="solar:export-bold-duotone" width={18} height={18} />
              }
              loading={exportLoading}
              onClick={handleExportExcel}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
      </Form>
      <Table
        columns={columns}
        size="small"
        loading={isLoading || isRefetching}
        rowKey="uuid"
        dataSource={data?.data || []}
        pagination={false}
        scroll={{ y: tableHeight, x: 1200 }}
      />
      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={data?.pagination.total}
        pageSize={params.limit}
        current={data?.pagination.page}
        onChange={handlePageChange}
        showSizeChanger={{
          showSearch: false,
        }}
        pageSizeOptions={["10", "20", "50"]}
        align="end"
      />
    </Card>
  );
}
